<template>
  <div class="agent-timeout-component">
    <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
      <div class="text-center">
        <!-- Timeout Icon -->
        <div class="flex items-center justify-center mb-4">
          <div class="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>
        
        <!-- Timeout Message -->
        <h4 class="text-yellow-800 text-base font-semibold mb-2">
          Kein Agent verfügbar
        </h4>
        <p class="text-yellow-700 text-sm mb-6">
          Momentan ist kein Agent verfügbar. Sie können warten oder uns eine E-Mail senden.
        </p>
        
        <!-- Action Buttons -->
        <div class="flex flex-col space-y-3">
          <!-- Wait Button -->
          <button
            @click="continueWaiting"
            :disabled="isProcessing"
            class="w-full px-6 py-3 bg-yellow-600 text-white font-medium rounded-lg transition-all duration-200 hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="!isProcessing || selectedAction !== 'wait'">Weiter warten</span>
            <span v-else class="flex items-center justify-center">
              <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Warten...
            </span>
          </button>
          
          <!-- Email Button -->
          <button
            @click="sendEmail"
            :disabled="isProcessing"
            class="w-full px-6 py-3 text-yellow-700 border border-yellow-300 bg-white font-medium rounded-lg transition-all duration-200 hover:bg-yellow-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="!isProcessing || selectedAction !== 'email'">E-Mail senden</span>
            <span v-else class="flex items-center justify-center">
              <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-yellow-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              E-Mail wird gesendet...
            </span>
          </button>
        </div>
        
        <!-- Success Message -->
        <div v-if="emailSent" class="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <div class="flex items-center justify-center space-x-2">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            <p class="text-green-800 text-sm font-medium">
              E-Mail erfolgreich gesendet!
            </p>
          </div>
          <p class="text-green-700 text-xs text-center mt-2">
            Wir werden uns bald bei Ihnen melden.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { sendEmail } from '../../lib/appwrite';

export default {
  name: 'AgentTimeout',
  props: {
    styles: {
      type: Object,
      default: () => ({
        buttonColor: '#000000'
      })
    },
    customer: {
      type: Object,
      required: true
    },
    conversation: {
      type: Object,
      required: true
    },
    messages: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      isProcessing: false,
      selectedAction: null,
      emailSent: false
    }
  },
  emits: ['continue-waiting', 'email-sent'],
  methods: {
    continueWaiting() {
      console.log('👀 User chose to continue waiting');
      this.selectedAction = 'wait';
      this.isProcessing = true;
      
      // Add system message
      this.$emit('continue-waiting');
      
      // Reset after a moment
      setTimeout(() => {
        this.isProcessing = false;
        this.selectedAction = null;
      }, 1500);
    },

    async sendEmail() {
      console.log('📧 User chose to send email');
      this.selectedAction = 'email';
      this.isProcessing = true;

      try {
        // Get the conversation summary from messages
        const conversationSummary = this.getConversationSummary();
        
        // 1. Send confirmation email to customer
        const customerEmailContent = `
Hallo ${this.customer.username},

vielen Dank für Ihre Anfrage. Da momentan kein Agent verfügbar ist, haben wir Ihre Nachricht per E-Mail erhalten und werden uns so bald wie möglich bei Ihnen melden.

Ihre Konversation:
${conversationSummary}

Mit freundlichen Grüßen,
Ihr Support-Team
        `.trim();

        await sendEmail({
          to: this.customer.email,
          subject: 'Ihre Chat-Anfrage - Wir melden uns bald',
          text: customerEmailContent
        });

        // 2. Send notification email to business/support
        const businessEmailContent = `
Neue Chat-Anfrage von Kunde (kein Agent verfügbar):

Kunde: ${this.customer.username}
E-Mail: ${this.customer.email}
Konversation ID: ${this.conversation.$id}

Konversation:
${conversationSummary}

Bitte kontaktieren Sie den Kunden zeitnah.
        `.trim();

        await sendEmail({
          to: 'support@chatly.com', // You can change this to your support email
          subject: `Chat-Anfrage von ${this.customer.username} (Timeout)`,
          text: businessEmailContent
        });

        console.log('✅ Emails sent successfully');
        this.emailSent = true;
        
        // Emit to parent
        this.$emit('email-sent', {
          customer: this.customer,
          conversation: this.conversation,
          conversationSummary
        });

        // Auto-close after showing success
        setTimeout(() => {
          this.$emit('chat-complete');
        }, 3000);

      } catch (error) {
        console.error('❌ Error sending emails:', error);
        alert('Fehler beim Senden der E-Mail. Bitte versuchen Sie es erneut.');
      } finally {
        this.isProcessing = false;
        this.selectedAction = null;
      }
    },

    getConversationSummary() {
      if (!this.messages || this.messages.length === 0) {
        return 'Keine Nachrichten verfügbar.';
      }

      const relevantMessages = this.messages.filter(msg => 
        msg.type === 'customer' || msg.type === 'ai' || msg.type === 'agent'
      );

      if (relevantMessages.length === 0) {
        return 'Kunde hat den Chat gestartet, aber noch keine Nachrichten gesendet.';
      }

      let summary = '';
      relevantMessages.forEach((msg, index) => {
        const sender = msg.type === 'customer' ? this.customer.username : 
                     msg.type === 'ai' ? 'NEX AI' : 
                     (msg.agentInfo?.agentName || 'Support Agent');
        
        summary += `${sender}: ${msg.content}\n`;
      });

      return summary.trim();
    }
  }
}
</script>

<style scoped>
.agent-timeout-component {
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>